import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailure = lazy(() => import("./pages/PaymentFailure"));
const Subscription = lazy(() => import("./pages/Subscription"));
const StudyMode = lazy(() => import("./pages/StudyMode"));
const LecturesPage = lazy(() => import("./pages/LecturesPage"));
const CreateQuiz = lazy(() => import("./pages/CreateQuiz"));
const TakeQuiz = lazy(() => import("./pages/TakeQuiz"));
const ManageQuiz = lazy(() => import("./pages/ManageQuiz"));
const MyQuizzes = lazy(() => import("./pages/MyQuizzes"));
const GroupAnswer = lazy(() => import("./pages/GroupAnswer"));
const CreateChatbot = lazy(() => import("./pages/CreateChatbot"));
const EditChatbot = lazy(() => import("./pages/EditChatbot"));
const MyChatbots = lazy(() => import("./pages/MyChatbots"));
const PublicChatbot = lazy(() => import("./pages/PublicChatbot"));
const CreateCommunity = lazy(() => import("./pages/CreateCommunity"));
const MyCommunities = lazy(() => import("./pages/MyCommunities"));
const CommunityDashboard = lazy(() => import("./pages/CommunityDashboard"));
const PublicCommunity = lazy(() => import("./pages/PublicCommunity"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/failure" element={<PaymentFailure />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/study" element={<StudyMode />} />
              <Route path="/study/lectures" element={<LecturesPage />} />
              <Route path="/study/quiz/create" element={<CreateQuiz />} />
              <Route path="/study/quizzes" element={<MyQuizzes />} />
              <Route path="/study/group-answer" element={<GroupAnswer />} />
              <Route path="/study/chatbot/create" element={<CreateChatbot />} />
              <Route path="/study/chatbot/:botId/edit" element={<EditChatbot />} />
              <Route path="/study/chatbots" element={<MyChatbots />} />
              <Route path="/bot/:shareCode" element={<PublicChatbot />} />
              <Route path="/study/community/create" element={<CreateCommunity />} />
              <Route path="/study/communities" element={<MyCommunities />} />
              <Route path="/study/community/:id/dashboard" element={<CommunityDashboard />} />
              <Route path="/community/:shareCode" element={<PublicCommunity />} />
              <Route path="/quiz/:quizId/take" element={<TakeQuiz />} />
              <Route path="/quiz/:quizId/manage" element={<ManageQuiz />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
